// App.js - Main application router for Travel+ CRM
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';

// Public Components
import LandingPage from './components/LandingPage';
import JoinAsAgency from './components/JoinAsAgency';
import IndependentLoginPage from './components/Auth/IndependentLoginPage';
import AgencyLoginPage from './components/Auth/AgencyLoginPage';
import AdminLoginPage from './components/Auth/AdminLoginPage';

// Agent Components
import AgentLayout from './components/Agent/AgentLayout';
import AgentDashboard from './components/Agent/AgentDashboard';
import ClientsManagement from './components/Agent/ClientsManagement';
import ItinerariesList from './components/Agent/ItinerariesList';
import CommissionsList from './components/Agent/CommissionsList';
import PaymentRemindersList from './components/Agent/PaymentRemindersList';
import AgentProfile from './components/Agent/AgentProfile';
import AgentAnalytics from './components/Agent/AgentAnalytics';
import QuoteBuilder from './components/Quotes/QuoteBuilder';
import QuotesList from './components/Quotes/QuotesList';
import WebsiteManagement from './components/Agent/WebsiteManagement';
import GroupsManagement from './components/Agent/Groups/GroupsManagement';
import { TaxFormSelection } from './components/TaxForms';

// Admin Components
import AdminLayout from './components/Admin/AdminLayout';
import AdminDashboard from './components/Admin/AdminDashboard';
import AgentManagement from './components/Admin/AgentManagement';
import CommissionsManagement from './components/Admin/CommissionsManagement';
import TaxFormsManagement from './components/Admin/TaxFormsManagement';
import AdminAnalytics from './components/Admin/AdminAnalytics';
import AgencySettings from './components/Admin/AgencySettings';
import AgencyAdminDashboard from './components/Admin/AgencyAdminDashboard';
import SuperAdminDashboard from './components/Admin/SuperAdminDashboard';

// Registration Components
import AgencyRegistration from './components/Registration/AgencyRegistration';
import AgencyRegistrationWithTax from './components/Registration/AgencyRegistrationWithTax';
import RegistrationSuccess from './components/Registration/RegistrationSuccess';

// Public Components
import PublicQuoteView from './components/Public/PublicQuoteView';
import PublicPaymentPage from './components/Public/PublicPaymentPage';
import PaymentSuccess from './components/Public/PaymentSuccess';

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/join-as-agency" element={<JoinAsAgency />} />
          <Route path="/quote/:quoteNumber" element={<PublicQuoteView />} />
          <Route path="/payment/:sessionId" element={<PublicPaymentPage />} />          
          <Route path="/payment-success/:confirmationNumber" element={<PaymentSuccess />} />
          <Route path="/registration-success" element={<RegistrationSuccess />} />          
          
          {/* Independent Agent Routes */}
          <Route path="/independent" element={<IndependentLoginPage />} />
          <Route path="/register/independent" element={<IndependentLoginPage />} />
          
          {/* Agency-specific Login Routes */}
          <Route path="/mst/login" element={<AgencyLoginPage agencyCode="MST" agency="MainStreet Travel Agency" color="blue" />} />
          <Route path="/yeti/login" element={<AgencyLoginPage agencyCode="YETI" agency="Yeti Travel Co." color="green" />} />
          <Route path="/bwk/login" element={<AgencyLoginPage agencyCode="BWK" agency="Boardwalk Travel" color="purple" />} />
          
          {/* Agency-specific Registration Routes */}
          <Route path="/mst/register" element={
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
              <div className="max-w-6xl w-full">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold text-xl">MST</span>
                  </div>
                  <h1 className="text-2xl font-bold text-gray-900">MainStreet Travel Agency</h1>
                  <p className="text-gray-600">Agent Registration</p>
                </div>
                <AgencyRegistrationWithTax 
                  agency="MainStreet Travel Agency" 
                  agencyCode="MST" 
                  onSuccess={() => window.location.href = '/mst/login'} 
                />
              </div>
            </div>
          } />
          <Route path="/yeti/register" element={
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
              <div className="max-w-6xl w-full">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold text-xl">YETI</span>
                  </div>
                  <h1 className="text-2xl font-bold text-gray-900">Yeti Travel Co.</h1>
                  <p className="text-gray-600">Agent Registration</p>
                </div>
                <AgencyRegistrationWithTax 
                  agency="Yeti Travel Co." 
                  agencyCode="YETI" 
                  onSuccess={() => window.location.href = '/yeti/login'} 
                />
              </div>
            </div>
          } />
          <Route path="/bwk/register" element={
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
              <div className="max-w-6xl w-full">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold text-xl">BWK</span>
                  </div>
                  <h1 className="text-2xl font-bold text-gray-900">Boardwalk Travel</h1>
                  <p className="text-gray-600">Agent Registration</p>
                </div>
                <AgencyRegistrationWithTax 
                  agency="Boardwalk Travel" 
                  agencyCode="BWK" 
                  onSuccess={() => window.location.href = '/bwk/login'} 
                />
              </div>
            </div>
          } />
          
          {/* Admin Login Routes */}
          <Route path="/admin/login" element={<AdminLoginPage type="super" />} />
          <Route path="/mst/admin" element={<AdminLoginPage type="agency" agencyCode="MST" />} />
          <Route path="/yeti/admin" element={<AdminLoginPage type="agency" agencyCode="YETI" />} />
          <Route path="/bwk/admin" element={<AdminLoginPage type="agency" agencyCode="BWK" />} />
          
          {/* Protected Agent Routes with Layout */}
          <Route path="/agent" element={
            <ProtectedRoute allowedRoles={['agent', 'agency_admin', 'super_admin']}>
              <AgentLayout />
            </ProtectedRoute>
          }>
            <Route path="dashboard" element={<AgentDashboard />} />
            <Route path="clients" element={<ClientsManagement />} />
            <Route path="quotes" element={<QuotesList />} />
            <Route path="quotes/new" element={<QuoteBuilder />} />
            <Route path="quotes/:id" element={<QuoteBuilder />} />
            <Route path="itineraries" element={<ItinerariesList />} />
            <Route path="commissions" element={<CommissionsList />} />
            <Route path="payment-reminders" element={<PaymentRemindersList />} />
            <Route path="groups" element={<GroupsManagement />} />
            <Route path="websites" element={<WebsiteManagement />} />
            <Route path="analytics" element={<AgentAnalytics />} />
            <Route path="profile" element={<AgentProfile />} />
            <Route path="tax-forms" element={<TaxFormSelection />} />
            <Route index element={<Navigate to="/agent/dashboard" replace />} />
          </Route>
          
          {/* Protected Admin Routes with Layout */}
          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={['agency_admin', 'super_admin']}>
              <AdminLayout />
            </ProtectedRoute>
          }>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="agents" element={<AgentManagement />} />
            <Route path="commissions" element={<CommissionsManagement />} />
            <Route path="tax-forms" element={<TaxFormsManagement />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="settings" element={<AgencySettings />} />
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
          </Route>
          
          {/* Legacy Agency Admin Routes */}
          <Route path="/agency/dashboard" element={
            <ProtectedRoute allowedRoles={['agency_admin']}>
              <Navigate to="/admin/dashboard" replace />
            </ProtectedRoute>
          } />
          
          {/* Legacy Super Admin Routes */}
          <Route path="/admin/super/dashboard" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <Navigate to="/admin/dashboard" replace />
            </ProtectedRoute>
          } />
          
          {/* Default redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
